import create from 'zustand';
import { lookUpService } from '../../Services/common/lookup.service';
import { ApiResponse } from '../../model/common/apiResponse';
import { Observable, of, BehaviorSubject } from 'rxjs';

const getWarehouseFromStorage = () => {
  try {
    const encrypted_warehouse = localStorage.getItem("warehouse");
    return encrypted_warehouse ? JSON.parse(atob(encrypted_warehouse)) : null;
  } catch (error) {
    console.error("Failed to parse warehouse from localStorage", error);
    return null;
  }
};

const initialWarehouse = getWarehouseFromStorage();
const warehouseSubject = new BehaviorSubject<Warehouse | null>(initialWarehouse);

export type Warehouse = {
  id: number;
  code: string;
  name: string;
};

interface WarehouseStore {  
  warehouses: Warehouse[];
  loading: boolean;
  error: string | null;
  fetchWarehouse: (skip_store?: boolean) => Promise<void>;
  onchangeWarehouse: (warehouse: Warehouse | null) => void;
  selectedWarehouse: Warehouse | null;
  getSelectedWarehouse: () => Observable<Warehouse | null>;
  reset: () => void;
}

const useWarehouseStore = create<WarehouseStore>((set, get) => {
  const fetchWarehouse = async (skip_store: boolean = false) => {
    const state = get(); 
    if (state?.warehouses.length > 0 && !skip_store) return;
    set({ loading: true, error: null });
    try {
      const response = await lookUpService.warehouseLookup();
      const data = response.data as ApiResponse<any>;
      set({ warehouses: data?.data?.results, loading: false });
    } catch (error: any) {
      set({ loading: false, error: error.message });
    }
  };

  const onchangeWarehouse = (warehouse: Warehouse | null) => {
    try {
      if (warehouse) {
        localStorage.setItem('warehouse', btoa(JSON.stringify(warehouse)));
      } else {
        localStorage.removeItem('warehouse');
      }
      set({ selectedWarehouse: warehouse });
      warehouseSubject.next(warehouse);
    } catch (error) {
      console.error("Failed to update warehouse", error);
    }
  };
  
  const getSelectedWarehouse = (): Observable<Warehouse | null> => {
    return warehouseSubject.asObservable();
  };

  const reset = () => {
    try {
      localStorage.removeItem('warehouse');
      set({ 
        warehouses: [], 
        loading: false, 
        error: null, 
        selectedWarehouse: null 
      });
      warehouseSubject.next(null);
    } catch (error) {
      console.error("Failed to reset warehouse store", error);
    }
  };

  return {
    warehouses: [], 
    loading: true,
    error: null,
    selectedWarehouse: initialWarehouse,
    fetchWarehouse,
    onchangeWarehouse,
    getSelectedWarehouse,
    reset
  };
});

export default useWarehouseStore;